import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Truck, Zap, IndianRupee, Wallet } from 'lucide-react';
import { useProducts } from '../context/ProductsContext';
import { useTheme } from '../context/ThemeContext';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { cities, cityBySlug } from '@shared/cities';
import NotFoundPage from './NotFoundPage';

const FEATURED_COUNT = 8;

export default function CityPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const city = cityBySlug[slug];

  const { products, isLoading } = useProducts();
  const { isDark } = useTheme();

  // Featured grid: best sellers first, then featured flag, then anything in stock.
  // Same set on every city page — no per-city filtering (uniform delivery scope).
  const featured = useMemo(() => {
    const inStock = products.filter((p) => p.inStock);
    const sellers = inStock.filter((p) => p.bestSeller);
    const featuredFlag = inStock.filter((p) => p.featured && !p.bestSeller);
    const rest = inStock.filter((p) => !p.bestSeller && !p.featured);
    return [...sellers, ...featuredFlag, ...rest].slice(0, FEATURED_COUNT);
  }, [products]);

  const otherCities = useMemo(
    () => cities.filter((c) => c.slug !== slug),
    [slug]
  );

  if (!city) return <NotFoundPage />;

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Cities', url: '/city' },
    { name: city.name, url: `/city/${city.slug}` },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: city.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={city.seoTitle}
        description={city.seoDescription}
        canonical={`/city/${city.slug}`}
        jsonLd={[buildBreadcrumbJsonLd(breadcrumbItems), faqJsonLd]}
      />

      <Breadcrumb
        items={[
          { name: 'Home', href: '/' },
          { name: 'Cities', href: '/city' },
          { name: city.name },
        ]}
      />

      {/* Header */}
      <div className={`border-b transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 text-sm mb-2">
            <MapPin className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{city.state}</span>
          </div>
          <h1 className={`text-3xl sm:text-4xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Buy Nicotine Pouches in {city.name}
          </h1>
          <p className={`mt-3 max-w-3xl leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {city.intro}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        {/* Delivery card — uniform copy site-wide */}
        <section
          className={`rounded-2xl border p-6 sm:p-8 ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
          }`}
        >
          <h2 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Delivery to {city.name}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DeliveryStat
              icon={<Truck className="w-5 h-5" />}
              title="Standard delivery"
              value="2–4 business days"
              isDark={isDark}
            />
            <DeliveryStat
              icon={<Zap className="w-5 h-5" />}
              title="Express delivery"
              value="Next-day available (extra)"
              isDark={isDark}
            />
            <DeliveryStat
              icon={<IndianRupee className="w-5 h-5" />}
              title="Free shipping"
              value="On orders over ₹5,000"
              isDark={isDark}
            />
            <DeliveryStat
              icon={<Wallet className="w-5 h-5" />}
              title="Payment"
              value="UPI, cards, COD on most orders"
              isDark={isDark}
            />
          </div>
        </section>

        {/* Featured products */}
        <section>
          <div className="flex items-end justify-between mb-5">
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Popular nicotine pouches in {city.name}
            </h2>
            <Link
              to="/store"
              className={`text-sm font-medium ${
                isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'
              }`}
            >
              View all →
            </Link>
          </div>

          {isLoading && featured.length === 0 ? (
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading products…</div>
          ) : featured.length === 0 ? (
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Currently restocking. Check back shortly.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {featured.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </section>

        {/* FAQ */}
        <section>
          <h2 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            FAQs — Nicotine pouches in {city.name}
          </h2>
          <div className="space-y-4">
            {city.faqs.map((f, i) => (
              <motion.div
                key={f.q}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className={`rounded-xl border p-5 ${
                  isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
                }`}
              >
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {f.q}
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {f.a}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Other cities */}
        <section
          className={`rounded-2xl border p-6 sm:p-8 ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
          }`}
        >
          <h2 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            We ship pan-India
          </h2>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Featured city guides — explore delivery info and popular picks in other Indian cities:
          </p>
          <div className="flex flex-wrap gap-2">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/city/${c.slug}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isDark
                    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function DeliveryStat({
  icon,
  title,
  value,
  isDark,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  isDark: boolean;
}) {
  return (
    <div
      className={`rounded-xl p-4 border ${
        isDark ? 'bg-gray-950 border-gray-800' : 'bg-gray-50 border-gray-100'
      }`}
    >
      <div className={`flex items-center gap-2 mb-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
        {icon}
        <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {title}
        </span>
      </div>
      <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
